import {
  fillDashboardTemplate,
  type DashboardDefinition,
  type DashboardNumberFormat,
  type DashboardTableColumn,
  type MetricFormat,
  type Provenance,
  type ResolvedDashboardWidget,
  type ResolvedKpiWidget,
  type ResolvedTableWidget,
} from "@t3tools/contracts";

import { ARTIFACT_TIME_ZONE } from "./artifactParameters";
import type {
  ArtifactCell,
  ArtifactColumn,
  ArtifactDataWidget,
  ArtifactSpec,
  ArtifactWidget,
} from "./artifactTypes";

export const EMPTY_VALUE = "—";

const integerFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 0 });
const decimalFormat = new Intl.NumberFormat("en-US", { maximumFractionDigits: 2 });
const currencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 2,
});
const wholeCurrencyFormat = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  maximumFractionDigits: 0,
});
const dateFormat = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
  year: "numeric",
});
const shortDateFormat = new Intl.DateTimeFormat("en-US", {
  timeZone: "UTC",
  month: "short",
  day: "numeric",
});
const timestampFormat = new Intl.DateTimeFormat("en-US", {
  timeZone: ARTIFACT_TIME_ZONE,
  month: "short",
  day: "numeric",
  year: "numeric",
  hour: "numeric",
  minute: "2-digit",
});

function formatSeconds(seconds: number): string {
  const total = Math.round(Math.abs(seconds));
  const sign = seconds < 0 ? "-" : "";
  if (total < 60) return `${sign}${total}s`;
  if (total < 3600) return `${sign}${Math.floor(total / 60)}m ${total % 60}s`;
  const minutes = Math.floor((total % 3600) / 60);
  return `${sign}${Math.floor(total / 3600)}h ${String(minutes).padStart(2, "0")}m`;
}

export function formatArtifactNumber(value: number, format: ArtifactColumn["format"]): string {
  switch (format) {
    case "integer":
      return integerFormat.format(value);
    case "percent":
      return `${value.toFixed(1)}%`;
    case "currency_usd":
      return (Math.abs(value) >= 1000 ? wholeCurrencyFormat : currencyFormat).format(value);
    case "seconds":
      return formatSeconds(value);
    default:
      return decimalFormat.format(value);
  }
}

function formatDate(value: string, short: boolean): string {
  const date = new Date(`${value.slice(0, 10)}T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return value;
  return (short ? shortDateFormat : dateFormat).format(date);
}

/** Formats a cell for display. `short` trims dates for chart axes. */
export function formatArtifactValue(
  value: ArtifactCell | undefined,
  column: ArtifactColumn | undefined,
  options: { short?: boolean } = {},
): string {
  if (value === null || value === undefined || value === "") return EMPTY_VALUE;
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (typeof value === "number") return formatArtifactNumber(value, column?.format);
  if (column?.type === "number") {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? formatArtifactNumber(parsed, column.format) : value;
  }
  if (column?.type === "date") return formatDate(value, options.short === true);
  if (column?.type === "timestamp") {
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? value : timestampFormat.format(date);
  }
  return value;
}

export function humanizeColumnName(name: string): string {
  const words = name.replaceAll("_", " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

export function artifactColumnLabel(column: ArtifactColumn | undefined, name: string): string {
  return column?.label ?? humanizeColumnName(name);
}

const METRIC_NUMBER_FORMAT: Record<MetricFormat, DashboardNumberFormat> = {
  count: "integer",
  percent: "percent",
  currency: "currency_usd",
};

/**
 * Columns a widget's resolved data uses. Dataset widgets read the spec's
 * declared columns, which carry reviewed labels and formats. Metric widgets
 * read the metric query's output columns, formatted by the metric.
 */
/** A metric value as a person reads it: 84.8%, 2,481, 48s, $12.50. */
export function formatMetricValue(value: number, format: MetricFormat | undefined): string {
  return formatArtifactNumber(value, format ? METRIC_NUMBER_FORMAT[format] : undefined);
}

export function artifactWidgetColumns(
  spec: Pick<ArtifactSpec, "datasets">,
  widget: ArtifactWidget,
  provenance: ReadonlyArray<Provenance> = [],
): ReadonlyMap<string, ArtifactColumn> {
  const columns = new Map<string, ArtifactColumn>();
  if (widget.kind === "text") return columns;
  if (widget.data.kind === "dataset") {
    const datasetId = widget.data.dataset;
    const dataset = spec.datasets.find((item) => item.id === datasetId);
    for (const column of dataset?.columns ?? []) columns.set(column.name, column);
    return columns;
  }
  const metric = provenance.find((item) => item.kind === "metric");
  const format = metric?.format ? METRIC_NUMBER_FORMAT[metric.format] : undefined;
  const { query } = widget.data;
  columns.set("metric", { name: "metric", type: "text", label: "Metric" });
  if (query.grain !== "total")
    columns.set("period", { name: "period", type: "date", label: "Period" });
  for (const dimension of query.dimensions) {
    columns.set(dimension, { name: dimension, type: "text" });
  }
  const value = (name: string, label: string | undefined): ArtifactColumn => ({
    name,
    type: "number",
    ...(format ? { format } : {}),
    ...(label ? { label } : {}),
  });
  columns.set("value", value("value", metric?.title));
  columns.set("compare_value", value("compare_value", "Comparison"));
  columns.set("numerator", { name: "numerator", type: "number", format: "integer" });
  columns.set("denominator", { name: "denominator", type: "number", format: "integer" });
  return columns;
}

/** Replaces `{column}` placeholders with the KPI's formatted footnote values. */
export function fillArtifactFootnote(
  template: string,
  resolved: ResolvedKpiWidget,
  columns: ReadonlyMap<string, ArtifactColumn>,
): string {
  const values = new Map(resolved.footnote.map((item) => [item.column, item.value]));
  return fillDashboardTemplate(template, (name) =>
    formatArtifactValue(values.get(name), columns.get(name)),
  );
}

/**
 * A table header: the spec's label with `{column}` placeholders filled from
 * the resolved first row (dates short, such as "Sep 21"), else the column's label.
 */
export function artifactTableColumnLabel(
  tableColumn: Pick<DashboardTableColumn, "column" | "label">,
  resolved: Pick<ResolvedTableWidget, "labelValues"> | null,
  columns: ReadonlyMap<string, ArtifactColumn>,
): string {
  if (tableColumn.label === undefined) {
    return artifactColumnLabel(columns.get(tableColumn.column), tableColumn.column);
  }
  const values = new Map(resolved?.labelValues?.map((item) => [item.column, item.value]));
  return fillDashboardTemplate(tableColumn.label, (name) =>
    formatArtifactValue(values.get(name), columns.get(name), { short: true }),
  );
}

export type ArtifactKpiDelta = { direction: "up" | "down"; text: string };

/**
 * Compares the KPI value with its `comparison` column. Direction is "up" when
 * the change is good for the metric so the kit colors it green.
 */
export function artifactKpiDelta(
  widget: Extract<ArtifactWidget, { kind: "kpi" }>,
  resolved: ResolvedKpiWidget,
  columns: ReadonlyMap<string, ArtifactColumn>,
): ArtifactKpiDelta | null {
  if (!widget.comparison) return null;
  const current = resolved.value.value;
  const baseline = resolved.comparison?.value;
  if (typeof current !== "number" || typeof baseline !== "number") return null;
  const change = current - baseline;
  const format = columns.get(widget.value)?.format;
  const magnitude =
    format === "percent"
      ? `${Math.abs(change).toFixed(1)} pts`
      : formatArtifactNumber(Math.abs(change), format);
  const sign = change > 0 ? "+" : change < 0 ? "−" : "±";
  const improved = widget.comparison.better === "higher" ? change >= 0 : change <= 0;
  return {
    direction: improved ? "up" : "down",
    text: `${sign}${magnitude} ${widget.comparison.label}`,
  };
}

/** Definitions a widget references: its own, then its table columns'. */
export function artifactWidgetDefinitions(
  spec: Pick<ArtifactSpec, "definitions">,
  widget: ArtifactWidget,
): DashboardDefinition[] {
  if (widget.kind === "text") return [];
  const ids = [
    widget.definition,
    ...(widget.kind === "table" ? widget.columns.map((column) => column.definition) : []),
  ];
  const byId = new Map(spec.definitions.map((definition) => [definition.id, definition]));
  return [...new Set(ids)]
    .flatMap((id) => (id ? [byId.get(id)] : []))
    .filter((definition): definition is DashboardDefinition => definition !== undefined);
}

/**
 * Rows for CSV export with column labels as headers, from what the widget
 * draws: a table's rows, or a chart's x values with one column per series.
 */
export function artifactCsvRows(
  widget: ArtifactDataWidget,
  resolved: ResolvedDashboardWidget,
  columns: ReadonlyMap<string, ArtifactColumn>,
): Array<Record<string, ArtifactCell>> {
  const label = (name: string) => artifactColumnLabel(columns.get(name), name);
  if (resolved.kind === "table" && widget.kind === "table") {
    const headers = resolved.columns.map((name) =>
      artifactTableColumnLabel(
        widget.columns.find((column) => column.column === name) ?? { column: name },
        resolved,
        columns,
      ),
    );
    return resolved.rows.map((cells) =>
      Object.fromEntries(headers.map((header, index) => [header, cells[index] ?? null])),
    );
  }
  if (resolved.kind === "chart") {
    return resolved.x.values.map((x, index) =>
      Object.fromEntries([
        [label(resolved.x.column), x],
        ...resolved.series.map((series) => [
          series.group !== undefined
            ? String(series.group)
            : (series.label ?? label(series.column)),
          series.values[index] ?? null,
        ]),
      ]),
    );
  }
  return [];
}

export function artifactExportFilename(title: string, widgetId: string, extension: string) {
  const slug = `${title}-${widgetId}`
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${slug}.${extension}`;
}
